import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

// Hooks
import {
  useLibraryData,
  useLibrarySelection,
  useLibraryActions,
  useTrashActions,
  useUploadFlow,
} from '../../hooks/library';

// Components
import FolderTree from './FolderTree';
import DocumentTable from './DocumentTable';
import TrashView from './TrashView';
import UploadPanel from './UploadPanel';
import AuditView from './AuditView';
import {
  FolderNameDialog,
  FolderDeleteDialog,
  FolderMoveDialog,
  DocumentRenameDialog,
  DocumentDeleteDialog,
  DocumentMoveDialog,
  PermanentDeleteDialog,
  EmptyTrashDialog,
  PreviewDialog,
  UploadDialog,
} from './dialogs/LibraryDialogs';

// Utils
import { getUniqueExtensions } from '../../lib/libraryUtils';

/**
 * Bibliotheek (Document Management System) Tab Component
 * 
 * Refactored version with extracted hooks and components.
 * Main responsibilities: composition, state wiring, and dialog management.
 */
const BibliotheekTab = () => {
  // === VIEW STATE ===
  const [viewMode, setViewMode] = useState('library'); // 'library' | 'trash' | 'audit'
  const [searchQuery, setSearchQuery] = useState('');
  const [filterExtension, setFilterExtension] = useState('all');

  // === DATA HOOKS ===
  const {
    folders, folderTree, loadingFolders, fetchFolders,
    documents, totalDocuments, loadingDocuments, fetchDocuments,
    trashFolders, trashDocuments, trashSummary, loadingTrash, fetchTrash,
    auditEvents, loadingAudit, fetchAudit,
  } = useLibraryData();

  // === SELECTION HOOKS ===
  const {
    selectedFolder, selectFolder, clearFolderSelection,
    expandedFolders, toggleFolderExpand,
    selectedTrashItems, toggleTrashSelection, clearTrashSelection, toggleSelectAllTrash,
  } = useLibrarySelection();

  // === ACTION HOOKS ===
  const {
    createFolder, renameFolder, deleteFolder, moveFolder, restoreFolder,
    renameDocument, deleteDocument, moveDocument, restoreDocument,
    downloadDocument, getPreviewUrl,
  } = useLibraryActions({ fetchFolders, fetchDocuments: () => fetchDocuments(selectedFolder?.id, searchQuery, filterExtension), fetchTrash });

  const {
    deletingItems,
    permanentDelete, permanentDeleteBulk, emptyTrash, retryDelete,
  } = useTrashActions({ fetchTrash, clearTrashSelection });

  // === UPLOAD HOOK ===
  const {
    uploadFiles, isUploading, queueFiles, removeFile, clearFiles, startUpload, pendingCount,
  } = useUploadFlow({ 
    folderId: selectedFolder?.id, 
    onUploadComplete: ({ successCount }) => {
      if (successCount > 0) {
        fetchDocuments(selectedFolder?.id, searchQuery, filterExtension);
        toast.success(`${successCount} bestand${successCount !== 1 ? 'en' : ''} geüpload`);
      }
    }
  });

  // === DIALOG STATE ===
  const [folderDialog, setFolderDialog] = useState({ open: false, mode: 'create', folder: null, parentId: null });
  const [deleteFolderDialog, setDeleteFolderDialog] = useState({ open: false, folder: null });
  const [moveFolderDialog, setMoveFolderDialog] = useState({ open: false, folder: null });
  const [moveFolderTarget, setMoveFolderTarget] = useState('');
  const [folderName, setFolderName] = useState('');

  const [documentDialog, setDocumentDialog] = useState({ open: false, mode: 'rename', document: null });
  const [documentName, setDocumentName] = useState('');
  const [deleteDocDialog, setDeleteDocDialog] = useState({ open: false, document: null });
  const [moveDocDialog, setMoveDocDialog] = useState({ open: false, document: null });
  const [moveTargetFolder, setMoveTargetFolder] = useState('');

  const [permanentDeleteDialog, setPermanentDeleteDialog] = useState({ open: false, items: [], mode: 'single' });
  const [emptyTrashDialog, setEmptyTrashDialog] = useState(false);
  
  const [previewDialog, setPreviewDialog] = useState({ open: false, document: null, url: null });
  const [uploadDialog, setUploadDialog] = useState(false);

  // === EFFECTS ===
  useEffect(() => {
    fetchFolders();
    fetchDocuments();
  }, [fetchFolders, fetchDocuments]);

  useEffect(() => {
    if (viewMode === 'trash') fetchTrash();
    if (viewMode === 'audit') fetchAudit();
  }, [viewMode, fetchTrash, fetchAudit]);

  useEffect(() => {
    fetchDocuments(selectedFolder?.id, searchQuery, filterExtension);
  }, [selectedFolder?.id, searchQuery, filterExtension, fetchDocuments]);

  // === HANDLERS ===
  const handleFolderSelect = useCallback((folder) => {
    selectFolder(folder);
  }, [selectFolder]);

  const handleOpenFolderDialog = useCallback((mode, folder = null, parentId = null) => {
    setFolderName(mode === 'rename' ? folder?.name || '' : '');
    setFolderDialog({ open: true, mode, folder, parentId });
  }, []);

  const handleFolderDialogConfirm = useCallback(async () => {
    const { mode, folder, parentId } = folderDialog;
    let success = false;
    
    if (mode === 'create') {
      success = await createFolder(folderName, parentId);
    } else if (mode === 'rename' && folder) {
      success = await renameFolder(folder.id, folderName);
    }
    
    if (success) {
      setFolderDialog({ open: false, mode: 'create', folder: null, parentId: null });
      setFolderName('');
    }
  }, [folderDialog, folderName, createFolder, renameFolder]);

  const handleDeleteFolderConfirm = useCallback(async () => {
    const { folder } = deleteFolderDialog;
    if (folder) {
      const success = await deleteFolder(folder.id);
      if (success) {
        if (selectedFolder?.id === folder.id) clearFolderSelection();
        setDeleteFolderDialog({ open: false, folder: null });
      }
    }
  }, [deleteFolderDialog, deleteFolder, selectedFolder, clearFolderSelection]);

  const handleMoveFolderConfirm = useCallback(async () => {
    const { folder } = moveFolderDialog;
    if (folder) {
      const success = await moveFolder(folder.id, moveFolderTarget === 'root' ? null : moveFolderTarget);
      if (success) {
        setMoveFolderDialog({ open: false, folder: null });
        setMoveFolderTarget('');
      }
    }
  }, [moveFolderDialog, moveFolderTarget, moveFolder]);

  const handleDocumentRenameConfirm = useCallback(async () => {
    const { document } = documentDialog;
    if (document) {
      const success = await renameDocument(document.id, documentName);
      if (success) {
        setDocumentDialog({ open: false, mode: 'rename', document: null });
        setDocumentName('');
      }
    }
  }, [documentDialog, documentName, renameDocument]);

  const handleDeleteDocConfirm = useCallback(async () => {
    const { document } = deleteDocDialog;
    if (document) {
      const success = await deleteDocument(document.id);
      if (success) {
        setDeleteDocDialog({ open: false, document: null });
      }
    }
  }, [deleteDocDialog, deleteDocument]);

  const handleMoveDocConfirm = useCallback(async () => {
    const { document } = moveDocDialog;
    if (document) {
      const success = await moveDocument(document.id, moveTargetFolder === 'root' ? null : moveTargetFolder);
      if (success) {
        setMoveDocDialog({ open: false, document: null });
        setMoveTargetFolder('');
      }
    }
  }, [moveDocDialog, moveTargetFolder, moveDocument]);

  const handlePreview = useCallback(async (doc) => {
    setPreviewDialog({ open: true, document: doc, url: null });
    const url = await getPreviewUrl(doc.id);
    if (url) setPreviewDialog(prev => ({ ...prev, url }));
  }, [getPreviewUrl]);

  const handlePermanentDeleteConfirm = useCallback(async () => {
    const { items, mode } = permanentDeleteDialog;
    if (mode === 'single' && items[0]) {
      await permanentDelete(items[0].id, items[0].title || items[0].original_name);
    } else {
      await permanentDeleteBulk(Array.from(selectedTrashItems));
    }
    setPermanentDeleteDialog({ open: false, items: [], mode: 'single' });
  }, [permanentDeleteDialog, selectedTrashItems, permanentDelete, permanentDeleteBulk]);

  const handleFilesSelected = useCallback((files) => {
    const { errors } = queueFiles(files);
    errors.forEach(err => toast.error(err));
  }, [queueFiles]);

  const handleStartUpload = useCallback(async () => {
    await startUpload(selectedFolder?.id);
  }, [startUpload, selectedFolder?.id]);

  // === DERIVED STATE ===
  const availableExtensions = getUniqueExtensions(documents);
  const breadcrumb = selectedFolder ? selectedFolder.name : 'Alle documenten';

  // === RENDER ===
  return (
    <div className="space-y-6">
      {/* Header with view mode tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'library' ? 'default' : 'outline'}
            onClick={() => setViewMode('library')}
            data-testid="btn-library"
          >
            📚 Bibliotheek
          </Button>
          <Button
            variant={viewMode === 'trash' ? 'default' : 'outline'}
            onClick={() => setViewMode('trash')}
            data-testid="btn-trash"
          >
            🗑️ Prullenbak
            {trashSummary.total_items > 0 && (
              <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-2">
                {trashSummary.total_items}
              </span>
            )}
          </Button>
          <Button
            variant={viewMode === 'audit' ? 'default' : 'outline'}
            onClick={() => setViewMode('audit')}
            data-testid="btn-audit"
          >
            📋 Audit Log
          </Button>
        </div>
        
        {viewMode === 'library' && (
          <div className="flex gap-2">
            <Button onClick={() => handleOpenFolderDialog('create', null, null)}>
              📁 Nieuwe map
            </Button>
            <Button onClick={() => setUploadDialog(true)} data-testid="btn-upload">
              📤 Uploaden
            </Button>
          </div>
        )}
      </div>

      {/* Library View */}
      {viewMode === 'library' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Folder tree sidebar */}
          <Card className="lg:col-span-1">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Mappen</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              {loadingFolders ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                </div>
              ) : (
                <>
                  <div
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer mb-2 ${
                      !selectedFolder ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => clearFolderSelection()}
                  >
                    <span>🏠</span>
                    <span className="text-sm font-medium">Alle documenten</span>
                  </div>
                  <FolderTree
                    folders={folderTree}
                    selectedFolder={selectedFolder}
                    expandedFolders={expandedFolders}
                    onSelect={handleFolderSelect}
                    onToggleExpand={toggleFolderExpand}
                    onCreateSubfolder={(f) => handleOpenFolderDialog('create', null, f.id)}
                    onRename={(f) => handleOpenFolderDialog('rename', f)}
                    onDelete={(f) => setDeleteFolderDialog({ open: true, folder: f })}
                    onMove={(f) => { setMoveFolderTarget(''); setMoveFolderDialog({ open: true, folder: f }); }}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {/* Documents area */}
          <Card className="lg:col-span-3">
            <CardHeader className="py-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-sm">{breadcrumb}</CardTitle>
                <div className="flex gap-2">
                  <Input
                    placeholder="Zoeken..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-40"
                  />
                  <Select value={filterExtension} onValueChange={setFilterExtension}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle types</SelectItem>
                      {availableExtensions.map(ext => (
                        <SelectItem key={ext} value={ext}>.{ext}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <DocumentTable
                documents={documents}
                loading={loadingDocuments}
                emptyMessage={selectedFolder ? 'Geen documenten in deze map' : 'Geen documenten gevonden'}
                onDocumentClick={() => {}}
                onPreview={handlePreview}
                onDownload={downloadDocument}
                onRename={(doc) => { setDocumentName(doc.title || ''); setDocumentDialog({ open: true, mode: 'rename', document: doc }); }}
                onMove={(doc) => { setMoveTargetFolder(''); setMoveDocDialog({ open: true, document: doc }); }}
                onDelete={(doc) => setDeleteDocDialog({ open: true, document: doc })}
              />
              {totalDocuments > documents.length && (
                <p className="text-center text-sm text-gray-400 mt-4">
                  Toont {documents.length} van {totalDocuments} documenten
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trash View */}
      {viewMode === 'trash' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">🗑️ Prullenbak</CardTitle>
          </CardHeader>
          <CardContent>
            <TrashView
              folders={trashFolders}
              documents={trashDocuments}
              summary={trashSummary}
              loading={loadingTrash}
              selectedItems={selectedTrashItems}
              deletingItems={deletingItems}
              onToggleSelection={toggleTrashSelection}
              onToggleSelectAll={() => toggleSelectAllTrash(trashDocuments.map(d => d.id))}
              onRestoreFolder={(f) => restoreFolder(f.id)}
              onRestoreDocument={(doc) => restoreDocument(doc.id, doc.title || doc.original_name)}
              onPermanentDelete={(doc) => setPermanentDeleteDialog({ open: true, items: [doc], mode: 'single' })}
              onRetryDelete={(doc) => retryDelete(doc.id, doc.title || doc.original_name)}
              onBulkDelete={() => setPermanentDeleteDialog({ 
                open: true, 
                items: trashDocuments.filter(d => selectedTrashItems.has(d.id)),
                mode: 'bulk'
              })}
              onEmptyTrash={() => setEmptyTrashDialog(true)}
            />
          </CardContent>
        </Card>
      )}

      {/* Audit View */}
      {viewMode === 'audit' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">📋 Audit Log</CardTitle>
          </CardHeader>
          <CardContent>
            <AuditView events={auditEvents} loading={loadingAudit} />
          </CardContent>
        </Card>
      )}

      {/* === DIALOGS === */}
      
      {/* Folder dialogs */}
      <FolderNameDialog
        open={folderDialog.open}
        mode={folderDialog.mode}
        folderName={folderName}
        parentFolder={folderDialog.parentId ? folders.find(f => f.id === folderDialog.parentId) : null}
        onNameChange={setFolderName}
        onConfirm={handleFolderDialogConfirm}
        onCancel={() => setFolderDialog({ open: false, mode: 'create', folder: null, parentId: null })}
      />
      <FolderDeleteDialog
        open={deleteFolderDialog.open}
        folder={deleteFolderDialog.folder}
        onConfirm={handleDeleteFolderConfirm}
        onCancel={() => setDeleteFolderDialog({ open: false, folder: null })}
      />
      <FolderMoveDialog
        open={moveFolderDialog.open}
        folder={moveFolderDialog.folder}
        folders={folders}
        targetFolderId={moveFolderTarget}
        onTargetChange={setMoveFolderTarget}
        onConfirm={handleMoveFolderConfirm}
        onCancel={() => setMoveFolderDialog({ open: false, folder: null })}
      />

      {/* Document dialogs */}
      <DocumentRenameDialog
        open={documentDialog.open}
        document={documentDialog.document}
        documentName={documentName}
        onNameChange={setDocumentName}
        onConfirm={handleDocumentRenameConfirm}
        onCancel={() => setDocumentDialog({ open: false, mode: 'rename', document: null })}
      />
      <DocumentDeleteDialog
        open={deleteDocDialog.open}
        document={deleteDocDialog.document}
        onConfirm={handleDeleteDocConfirm}
        onCancel={() => setDeleteDocDialog({ open: false, document: null })}
      />
      <DocumentMoveDialog
        open={moveDocDialog.open}
        document={moveDocDialog.document}
        folders={folders}
        targetFolderId={moveTargetFolder}
        onTargetChange={setMoveTargetFolder}
        onConfirm={handleMoveDocConfirm}
        onCancel={() => setMoveDocDialog({ open: false, document: null })}
      />

      {/* Trash dialogs */}
      <PermanentDeleteDialog
        open={permanentDeleteDialog.open}
        items={permanentDeleteDialog.items}
        mode={permanentDeleteDialog.mode}
        deleting={deletingItems}
        onConfirm={handlePermanentDeleteConfirm}
        onCancel={() => setPermanentDeleteDialog({ open: false, items: [], mode: 'single' })}
      />
      <EmptyTrashDialog
        open={emptyTrashDialog}
        itemCount={trashSummary.total_items}
        deleting={deletingItems}
        onConfirm={async () => { await emptyTrash(); setEmptyTrashDialog(false); }}
        onCancel={() => setEmptyTrashDialog(false)}
      />

      {/* Preview dialog */}
      <PreviewDialog
        open={previewDialog.open}
        previewUrl={previewDialog.url}
        document={previewDialog.document}
        onClose={() => setPreviewDialog({ open: false, document: null, url: null })}
      />

      {/* Upload dialog */}
      <UploadDialog
        open={uploadDialog}
        onClose={() => { setUploadDialog(false); clearFiles(); }}
      >
        <UploadPanel
          files={uploadFiles}
          isUploading={isUploading}
          pendingCount={pendingCount}
          onFilesSelected={handleFilesSelected}
          onRemoveFile={removeFile}
          onStartUpload={handleStartUpload}
          onClearFiles={clearFiles}
        />
      </UploadDialog>
    </div>
  );
};

export default BibliotheekTab;
